// Sugalarm — kiosk voice agent hook (cycle 100).
//
// Modeled on `useWifiAgent`. Owns reachability + wake-word state on the
// loopback agent, and the one-way agent→SPA wake event stream. Surface:
//   - `available` is true ONLY when (a) variant === "kiosk", (b) GET
//     /health succeeds, and (c) the agent reports `voice_supported: true`.
//     False on any non-kiosk surface, on an unreachable agent, on an
//     agent built without the access key / `.ppn` files, or while voice
//     is env-disabled. Web/mobile make ZERO calls.
//   - `options` / `activeKey` are the agent's discovered wake words +
//     the one currently loaded (from `/voice/wake/options` + the SSE
//     `ready` frame).
//   - `lastWakeTs` is the epoch-ms of the most recent `wake` SSE event —
//     the SPA fires the earcon + banner pulse off changes to it.
//   - `setWakeWord(key)` hot-reloads the agent and returns the result.
//   - `setWakePaused(paused, ttlMs?)` drives the echo guard around TTS.
//
// Variant-gated inside the hook (not the call site) for Rules of Hooks —
// exactly like `useWifiAgent`.

import { useCallback, useEffect, useRef, useState } from "react";

import * as voiceApi from "../api/voice";
import type { ApiResult } from "../lib/types";
import type { FrameVariant } from "./useFrameVariant";

const REACHABILITY_POLL_MS = 15_000;

/** Cycle 101 — a recognized intent (or a miss) from the agent's Rhino
 *  layer, surfaced off the SSE stream to the dispatcher. Each carries the
 *  agent's epoch-ms `ts`, which the dispatcher uses as the dedupe key so
 *  two identical intents in a row (e.g. "yes" twice) each fire. */
export type VoiceIntentEvent =
  | {
      type: "intent";
      intent: string;
      slots: Record<string, string>;
      ts: number;
    }
  | { type: "intent_miss"; ts: number }
  // Cycle 102 — open-vocabulary carb question (Leopard transcript). The
  // dispatcher sends `text` to the Fly carb estimator.
  | { type: "carb_query"; text: string; ts: number };

export interface VoiceAgentState {
  available: boolean;
  options: voiceApi.WakeOption[];
  activeKey: string | null;
  lastWakeTs: number | null;
  /** Cycle 101 — the most recent intent / miss from the SSE stream. The
   *  dispatcher (`useVoiceDispatch`) consumes it via an effect keyed on
   *  `lastIntent.ts`. Null until the first intent (or when no `.rhn` is
   *  loaded — wake-only). */
  lastIntent: VoiceIntentEvent | null;
  setWakeWord: (key: string) => Promise<ApiResult<voiceApi.WakeSetResult>>;
  setWakePaused: (
    paused: boolean,
    ttlMs?: number,
  ) => Promise<ApiResult<voiceApi.WakePausedResult>>;
}

export function useVoiceAgent(variant: FrameVariant): VoiceAgentState {
  const [available, setAvailable] = useState(false);
  const [options, setOptions] = useState<voiceApi.WakeOption[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [lastWakeTs, setLastWakeTs] = useState<number | null>(null);
  const [lastIntent, setLastIntent] = useState<VoiceIntentEvent | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  // Probe reachability + load wake options. Sets `available` and seeds
  // `options` / `activeKey`. Never runs network on a non-kiosk surface.
  const probe = useCallback(async () => {
    if (variant !== "kiosk") {
      setAvailable(false);
      setOptions([]);
      setActiveKey(null);
      return;
    }
    const h = await voiceApi.getHealth();
    if (!h.ok || !h.data.voice_supported) {
      setAvailable(false);
      return;
    }
    setAvailable(true);
    const o = await voiceApi.getWakeOptions();
    if (o.ok) {
      setOptions(o.data.options);
      setActiveKey(o.data.active);
    }
  }, [variant]);

  useEffect(() => {
    void probe();
  }, [probe]);

  // Re-probe every 15 s while unreachable (cycle-34.12 self-heal cadence,
  // matching the volume + wifi hooks).
  useEffect(() => {
    if (variant !== "kiosk") return;
    if (available) return;
    const id = window.setInterval(() => {
      void probe();
    }, REACHABILITY_POLL_MS);
    return () => window.clearInterval(id);
  }, [variant, available, probe]);

  // Open the wake event stream while available; tear it down otherwise.
  // EventSource reconnects automatically on a transient drop; on a hard
  // CLOSED error we flip `available` so the re-probe loop re-establishes.
  useEffect(() => {
    if (!available) {
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      return;
    }
    const es = new EventSource(voiceApi.EVENTS_URL);
    sourceRef.current = es;
    es.onmessage = (evt: MessageEvent) => {
      try {
        const data = JSON.parse(evt.data) as {
          type?: string;
          ts?: number;
          active?: string | null;
          intent?: string;
          slots?: Record<string, string>;
          text?: string;
        };
        if (data.type === "wake" && typeof data.ts === "number") {
          setLastWakeTs(data.ts);
        } else if (data.type === "ready") {
          if (data.active !== undefined) setActiveKey(data.active ?? null);
        } else if (
          data.type === "intent" &&
          typeof data.intent === "string" &&
          typeof data.ts === "number"
        ) {
          // Cycle 101 — recognized intent. The dispatcher consumes it.
          setLastIntent({
            type: "intent",
            intent: data.intent,
            slots: data.slots ?? {},
            ts: data.ts,
          });
        } else if (data.type === "intent_miss" && typeof data.ts === "number") {
          setLastIntent({ type: "intent_miss", ts: data.ts });
        } else if (
          data.type === "carb_query" &&
          typeof data.text === "string" &&
          typeof data.ts === "number"
        ) {
          // Cycle 102 — open-vocabulary carb question.
          setLastIntent({ type: "carb_query", text: data.text, ts: data.ts });
        }
      } catch {
        /* ignore malformed frames */
      }
    };
    es.onerror = () => {
      // Native EventSource keeps retrying while readyState is CONNECTING.
      // Only a terminal CLOSED means the stream is dead — flip available
      // so the 15 s probe loop re-checks /health and reopens.
      if (es.readyState === EventSource.CLOSED) {
        setAvailable(false);
      }
    };
    return () => {
      es.close();
      if (sourceRef.current === es) sourceRef.current = null;
    };
  }, [available]);

  const setWakeWord = useCallback(async (key: string) => {
    const r = await voiceApi.setWakeWord(key);
    if (r.ok) setActiveKey(r.data.active);
    return r;
  }, []);

  const setWakePaused = useCallback(
    (paused: boolean, ttlMs?: number) => voiceApi.setWakePaused(paused, ttlMs),
    [],
  );

  return {
    available,
    options,
    activeKey,
    lastWakeTs,
    lastIntent,
    setWakeWord,
    setWakePaused,
  };
}
